import { buildUser } from '~/app/_repositories/query-builder/user-query-builder';
import { convertICommentSectionResponseToStoryCommentListWithTotalCount } from '~/common/converter/story/comment';
import type { StoryCommentsWithTotalCount } from '~/common/types/story-comment';
import { convertIErrorResponseToAppError } from '~/infra/api/error-handler';
import { getQueriesForClient } from '@app/_repositories/query-service';

type Params = {
  storyId: string;
  commentId: string;
  cursor?: string;
  limit?: number;
};

export type CommentsResult = StoryCommentsWithTotalCount & {
  storyId: string;
  nextCursor?: string;
  hasNext: boolean;
  currentPage: number;
};

export const getCommentReplyListRequest = async ({
  storyId,
  commentId,
  cursor,
  limit,
}: Params): Promise<CommentsResult> => {
  const commentBriefRequestId = `comment-${storyId}-${commentId}-${
    cursor ?? ''
  }`;

  const queryServiceResponse = await getQueriesForClient({
    request_list: [
      {
        request_id: commentBriefRequestId,
        comment_request: {
          id: commentId,
          require_comment: {
            require_reply_comment_page: {
              include_deleted: true,
              cursor: {
                limit,
                cursor,
                require_cursor_info: {
                  require_has_next_page: true,
                  require_total_count: true,
                  require_end_cursor: true,
                },
              },
              require_comment_list: {
                require_id: true,
                require_deleted_by: true,
                require_user: buildUser(),
                require_text_body: true,
                require_stamp_body: {
                  require_image: {
                    require_serving_url: true,
                  },
                },
                require_created_at: true,
                require_like_count: true,
                require_liked_by_me: true,
                require_liked_by_author: true,
                require_reply_comment_page: {
                  cursor: {
                    require_cursor_info: {
                      require_total_count: true,
                    },
                  },
                },
              },
            },
          },
        },
      },
    ],
  });

  const targetResponse = queryServiceResponse.response_list.find(
    (res) => res.request_id === commentBriefRequestId
  );

  if (targetResponse?.error) {
    throw convertIErrorResponseToAppError(targetResponse.error);
  }

  const commentListResponse = targetResponse?.comment?.reply_comment_page;

  const currentPage = !cursor || Number.isNaN(cursor) ? 0 : Number(cursor);

  return commentListResponse
    ? {
        ...convertICommentSectionResponseToStoryCommentListWithTotalCount(
          commentListResponse
        ),
        storyId,
        nextCursor: commentListResponse.cursor?.end_cursor,
        hasNext: commentListResponse.cursor?.has_next_page ?? false,
        currentPage: currentPage,
      }
    : {
        totalCount: 0,
        comments: [],
        storyId,
        nextCursor: undefined,
        hasNext: false,
        currentPage: currentPage,
      };
};
